import { createContext, ReactNode, useContext, useEffect } from 'react';

/**
 * Asks before leaving the page while `when` holds. The host app supplies it,
 * since blocking a navigation needs its router and its confirm dialog.
 */
export type UseNavigatePrompt = (when: boolean) => void;

const noPrompt: UseNavigatePrompt = () => undefined;

const NavigatePromptContext = createContext<UseNavigatePrompt>(noPrompt);

/**
 * Injected via the `useNavigatePrompt` prop on the knowledge management entry.
 * The value is called as a hook, so it must stay the same for the lifetime of
 * the provider.
 */
export function NavigatePromptProvider({
  value,
  children,
}: {
  value: UseNavigatePrompt | undefined;
  children: ReactNode;
}): JSX.Element {
  return (
    <NavigatePromptContext.Provider value={value ?? noPrompt}>
      {children}
    </NavigatePromptContext.Provider>
  );
}

/**
 * Guards unsaved changes while `shouldPrompt` holds: navigating inside the app asks
 * through the host's prompt, and closing or reloading the tab through the
 * browser's own.
 */
export function useNavigatePrompt(shouldPrompt: boolean): void {
  const useHostPrompt = useContext(NavigatePromptContext);
  useHostPrompt(shouldPrompt);

  useEffect(() => {
    if (!shouldPrompt) return undefined;

    const onBeforeUnload = (unloadEvent: BeforeUnloadEvent) => {
      unloadEvent.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [shouldPrompt]);
}
